import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { usePollWhile } from '../common/useLiveReload';
import { LoadingRegion } from '../common/icons';
import { releaseBadge, releaseLine } from './releaseState';
import type { ReleaseDetail, ReleaseSummary } from '../../types';

/**
 * What this site has published, and whether it worked.
 *
 * Before this the product could not answer "what changed?" at all:
 * `DeploymentPlan.last_run_details` held a summary of the LAST run only, so
 * every deploy erased the one before it. That is the first question anybody asks
 * when a site looks wrong.
 *
 * Self-fetching and read-only, matching `SiteGitHubRepos` and
 * `SiteStructurePanel`. A release is written by the deploy and never edited.
 */
export default function SiteReleases({ siteId }: { siteId: number }) {
  const [releases, setReleases] = useState<ReleaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ReleaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      setReleases(await api.getReleases(siteId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the deploy history');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);
  // A DEPLOY IN PROGRESS SENDS NO LIVE EVENT, so while one has not finished the
  // list looks again, quietly. It used to stay "in progress" until a reload.
  usePollWhile(releases.some(r => !r.finished_at), () => void load(true), 5_000);

  const open = async (id: number) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await api.getRelease(id));
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <LoadingRegion label="Loading deploy history…" />;

  if (error) {
    return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  }

  if (!releases.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Nothing has been deployed to this site yet. Every deploy from here on is
        recorded - what went out, when, and whether it worked.
      </p>
    );
  }

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      {releases.map((release) => {
        const badge = releaseBadge(release.state);
        const when = release.finished_at || release.started_at;
        return (
          <div key={release.id}>
            <button
              onClick={() => open(release.id)}
              className="w-full text-left py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 -mx-2 rounded"
            >
              <span
                className={`shrink-0 mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap ${badge.className}`}
              >
                {badge.label}
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-gray-900 dark:text-gray-100">
                  {releaseLine(release)}
                </span>
                <span className="block text-xs text-gray-400 dark:text-gray-500">
                  {new Date(when).toLocaleString()}
                  {release.plan_name && ` · ${release.plan_name}`}
                </span>
              </span>
              <span className="ml-auto shrink-0 text-xs text-gray-400 dark:text-gray-500">
                {openId === release.id ? '▾' : '▸'}
              </span>
            </button>

            {openId === release.id && (
              <div className="pb-3 pl-2">
                {detailLoading ? (
                  <LoadingRegion label="Loading…" className="py-4" />
                ) : detail ? (
                  <>
                    {detail.pages.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No pages were sent in this deploy.
                      </p>
                    ) : (
                      <ul className="space-y-1">
                        {/* Failures first — a release is opened when something
                            went wrong, and the thing you came for should not be
                            forty rows down. The server orders it that way. */}
                        {detail.pages.map((page, i) => (
                          <li key={i} className="text-sm flex items-start gap-2">
                            <span
                              className={
                                page.success
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }
                            >
                              {page.success ? '✓' : '✕'}
                            </span>
                            <span className="min-w-0">
                              <span className="text-gray-900 dark:text-gray-100">
                                {/* The title AS IT WAS. This row does not
                                    re-read the page, so renaming or deleting it
                                    later cannot rewrite this history. */}
                                {page.title || page.path || 'Untitled'}
                              </span>
                              {page.path && (
                                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                  {page.path}
                                </span>
                              )}
                              {!page.success && page.detail && (
                                <span className="block text-xs text-red-600 dark:text-red-400">
                                  {page.detail}
                                </span>
                              )}
                            </span>
                            {page.public_url && (
                              <a
                                href={page.public_url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-auto shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                open ↗
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Could not load this deploy.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
